import React from 'react';
import { Link } from 'react-router-dom';
import { FixedSizeList } from 'react-window';
import { AutoSizer } from 'react-virtualized';
import { useTable, useFlexLayout, useResizeColumns, useRowState } from 'react-table';
import { ITicker } from '@kattana-io/common';
import Text from '../../atoms/Text';
import Clock from './Clock';
import { useNewPairsTableData } from './useNewPairsTableData';
import TableDateField from '../../components/TableComponents/Date';
import PairWithIcons from '../../components/TableComponents/PairWithIcons';
import { toFixed6 } from '../../components/DexTrades/formatters';
import useTicker from '../../hooks/useTicker';
import { formatUSD } from '../../components/Modal/TickersModal/helper';
import TableContractField from '../../components/TableComponents/Contract';

interface INewPairsTableProps {
    pairs: ITicker[]
}

const liquidityAdded = (ticker: ITicker): boolean =>
  (ticker.updated_at > 0 || ticker.liquidity > 0);

const NewPairsTable = ({pairs}: INewPairsTableProps) => {
  const { columns, data } = useNewPairsTableData({ pairs });

  const tableInstance = useTable({ columns: columns, data },
    useFlexLayout,
    useResizeColumns,
    useRowState
  );


  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = tableInstance;

  const HeaderGroup = ({ headerGroup }: any) => <div className='tr thr' {...headerGroup.getHeaderGroupProps()}>
    {
      headerGroup.headers.map((column: any) => (
        <div className='th' key={JSON.stringify(column)} {...column.getHeaderProps()}>
          <Text size={12} className="text--grey-600 text--weight-600">
            {column.render('Header')}
          </Text>
        </div>
      ))}
  </div>;

  const RenderRow = React.useCallback(
    ({ index, style }) => {
      const row: any = rows[index];
      prepareRow(row);
      return (
        <Link
          to={`/${row.original.chain}/${row.original.address}`}
          {...row.getRowProps()}
          style={{ ...style, display: 'flex', textDecoration: 'none' }}
        >
          {row.cells.map((cell: any) =>
            (<div className='td' key={index} {...cell.getCellProps()}>{Cell(cell)}</div>)
          )}
        </Link>
      );
    },
    [prepareRow, rows]
  );

  const Cell = (cell: any): React.ReactNode => {
    const ticker = useTicker(cell.row.original);

    switch (cell.column.id) {
    case 'name':
      return <PairWithIcons chain={cell.row.original.chain} name={cell.value} base_address={cell.row.original.base_address} quote_address={cell.row.original.quote_address}/>;
    case 'pool_created':
      if (cell.value) {
        return <Clock timestamp={cell.value * 1000}/>;
      }
      return null;
    case 'priceUSD':
      return <Text size={12} className="text--grey-600">${toFixed6(cell.value)}</Text>;
    case 'initial_liquidity':
      return <TableDateField timestamp={cell.row.original.pool_created * 1000}/>;
    case 'liquidity':
      return <div className={`text--12 ${liquidityAdded(cell.row.original) ? 'text--green-600' : 'text--grey-600'}`}>{formatUSD(cell.value)}</div>;
    case 'base_reserve':
      return <Text size={12} className="text--grey-600">{ticker.base_reserve} {ticker.base}</Text>;
    case 'quote_reserve':
      return <Text size={12} className="text--grey-600">{ticker.quote_reserve} {ticker.quote}</Text>;
    case 'contract':
      return <TableContractField address={cell.value} chain={cell.row.original.chain} />;
    default: return <div className='text--12 text--grey-600'>{cell.value}</div>;
    }
  };


  return <>
    {(!data.length ? <div className='tableCell centrify'>No data found</div> :
      <div className='newPairs__table' {...getTableProps()}>
        <div className='thead'>
          {
            headerGroups.map((headerGroup, index) => (
              <HeaderGroup key={index} headerGroup={headerGroup} />
            ))}
        </div>
        <div className='tbody' {...getTableBodyProps()}>
          <AutoSizer>
            {({ height, width }) => <FixedSizeList
              height={height}
              itemCount={rows.length}
              itemSize={40}
              width={width}
              className="tbody__autosizer newPairs__table"
              style={{overflow: 'overlay'}}
            >
              {RenderRow}
            </FixedSizeList>}
          </AutoSizer>
        </div>
      </div>)
    }
  </>;
};

export default NewPairsTable;
